import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, BarChart3, Shield, TrendingUp, Check, KeyRound, Eye, EyeOff, Sparkles, Star, FileText, Receipt, Wallet, PieChart, ListChecks, LineChart as LineChartIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
export default function Auth() {
  const {
    signIn,
    resetPassword,
    updatePassword,
    loading,
    isRecoveryMode,
    user
  } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [activeTab, setActiveTab] = useState<'inicio' | 'planos'>('inicio');
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'plus' | null>(null);
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Show/hide password states
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);



  React.useEffect(() => {
    document.title = "Radar Financeiro - Controle Total das suas Finanças";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Transforme a gestão do seu negócio com o Radar Financeiro. PDV, controle de estoque, gestão financeira e relatórios inteligentes.');
  }, []);

  // Detect recovery/invite mode from URL: /auth?token_hash=XXX&type=recovery|invite
  const [showPasswordReset, setShowPasswordReset] = React.useState(false);
  const [isInviteMode, setIsInviteMode] = React.useState(false);
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [passwordError, setPasswordError] = React.useState('');
  React.useEffect(() => {
    // Check for URL error parameters first
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const queryParams = new URLSearchParams(window.location.search);
    const errorCode = hashParams.get('error_code') || queryParams.get('error_code');
    const errorDesc = hashParams.get('error_description') || queryParams.get('error_description');
    const typeParam = queryParams.get('type') || hashParams.get('type');
    if (errorCode || errorDesc) {
      logger.log('[Auth] Error in URL detected');
      setPasswordError(errorDesc || 'Link expirado ou inválido. Solicite um novo.');
      sessionStorage.removeItem('password_recovery_mode');
      sessionStorage.removeItem('invite_mode');
      setShowPasswordReset(true); // Show form to display error
      return;
    }

    // Check for invite mode
    const isInviteFromStorage = sessionStorage.getItem('invite_mode') === 'true';
    const isInviteFromURL = typeParam === 'invite';
    if (isInviteFromStorage || isInviteFromURL) {
      logger.log('[Auth] Invite mode detected');
      setIsInviteMode(true);
      setShowPasswordReset(true);
      return;
    }

    // Check for recovery mode
    const isRecoveryFromStorage = sessionStorage.getItem('password_recovery_mode') === 'true';
    logger.log('[Auth] Recovery check:', {
      isRecoveryFromStorage,
      isRecoveryMode
    });
    if (isRecoveryFromStorage || isRecoveryMode) {
      setShowPasswordReset(true);
    }
  }, [isRecoveryMode]);

  // Get email from session when in invite mode
  React.useEffect(() => {
    const getInviteEmail = async () => {
      if (isInviteMode) {
        const {
          data: {
            session
          }
        } = await supabase.auth.getSession();
        if (session?.user?.email) {
          setInviteEmail(session.user.email);
          logger.log('[Auth] Got invite email from session');
        }
      }
    };
    getInviteEmail();
  }, [isInviteMode]);

  // Combined auth setup check - recovery OR invite mode
  const inAuthSetupMode = isRecoveryMode || isInviteMode || showPasswordReset || sessionStorage.getItem('password_recovery_mode') === 'true' || sessionStorage.getItem('invite_mode') === 'true';

  // Redirect authenticated users (but NEVER in auth setup mode)
  React.useEffect(() => {
    logger.log('[Auth] Redirect check:', {
      user: !!user,
      inAuthSetupMode,
      isInviteMode
    });
    // Don't redirect if in ANY form of auth setup mode
    if (user && !inAuthSetupMode && !showPasswordReset) {
      // Consume `?next=` when present (OAuth consent flow etc.).
      // Only allow same-origin relative paths to avoid open-redirects.
      const params = new URLSearchParams(window.location.search);
      const rawNext = params.get('next');
      const next = rawNext && rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : null;
      navigate(next ?? '/');
    }
  }, [user, inAuthSetupMode, showPasswordReset, navigate]);
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) return;
    setIsLoading(true);
    try {
      await signIn(loginData.email, loginData.password);
    } catch (error) {
      // Error handling is done in the AuthContext
    } finally {
      setIsLoading(false);
    }
  };
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setIsLoading(true);
    try {
      await resetPassword(forgotEmail);
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      // Error handling is done in the AuthContext
    } finally {
      setIsLoading(false);
    }
  };
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    if (!newPassword || !confirmPassword) {
      setPasswordError('Preencha todos os campos');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('A senha deve ter no mínimo 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('As senhas não coincidem');
      return;
    }
    setIsLoading(true);
    try {
      logger.log('[Auth] Starting password update');

      // Get current session - should be already set by AuthContext
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      logger.log('[Auth] Current session:', session ? 'Found' : 'NONE');
      if (!session) {
        logger.error('[Auth] No active session for password update');
        setPasswordError('Link expirado ou inválido. Solicite um novo link de recuperação.');
        sessionStorage.removeItem('password_recovery_mode');
        setIsLoading(false);
        return;
      }

      // Update password
      const {
        error
      } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) {
        logger.error('[Auth] Password update error');
        if (error.message.toLowerCase().includes('session') || error.message.toLowerCase().includes('token')) {
          setPasswordError('Sessão expirada. Solicite um novo link de recuperação.');
          sessionStorage.removeItem('password_recovery_mode');
        } else {
          setPasswordError('Erro ao atualizar senha: ' + error.message);
        }
        setIsLoading(false);
        return;
      }
      logger.log('[Auth] Password updated successfully');

      // Success - clear ALL auth setup mode flags and redirect
      sessionStorage.removeItem('password_recovery_mode');
      sessionStorage.removeItem('invite_mode');
      sessionStorage.removeItem('recovery_token_hash');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordReset(false);
      setIsInviteMode(false);

      // Force full page reload to reset all states
      window.location.href = '/';
    } catch (error: any) {
      logger.error('[Auth] Exception during password update');
      setPasswordError(error?.message || 'Erro ao atualizar senha. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };
  const handlePlanRedirect = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>;
  }

  // Password Setup Mode - Show form for password reset OR invite (create account)
  if (inAuthSetupMode) {
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-white border border-gray-200 shadow-xl">
          <form onSubmit={handleUpdatePassword}>
            <CardHeader className="text-center space-y-2">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <KeyRound className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">
                {isInviteMode ? 'Criar sua Conta' : 'Definir Nova Senha'}
              </CardTitle>
              <CardDescription className="text-gray-600">
                {isInviteMode ? 'Bem-vindo ao Radar Financeiro! Defina sua senha para acessar o sistema.' : 'Digite sua nova senha para acessar o sistema'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {passwordError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{passwordError}</p>
                </div>}
              
              {/* Show email for invite mode */}
              {isInviteMode && inviteEmail && <div className="space-y-2">
                  <Label className="text-gray-700">E-mail (seu login)</Label>
                  <Input type="email" value={inviteEmail} disabled className="h-11 bg-gray-100 border-gray-300 text-gray-700" />
                </div>}
              
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-gray-700">
                  {isInviteMode ? 'Crie sua Senha' : 'Nova Senha'}
                </Label>
                <div className="relative">
                  <Input id="new-password" type={showNewPassword ? "text" : "password"} placeholder={isInviteMode ? "Crie uma senha segura" : "Digite sua nova senha"} value={newPassword} onChange={e => {
                  setNewPassword(e.target.value);
                  setPasswordError('');
                }} className="h-11 bg-gray-50 border-gray-300 focus:border-primary pr-10" required minLength={6} />
                  <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Mínimo 6 caracteres
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-gray-700">Confirmar Senha</Label>
                <div className="relative">
                  <Input id="confirm-password" type={showConfirmPassword ? "text" : "password"} placeholder="Confirme sua senha" value={confirmPassword} onChange={e => {
                  setConfirmPassword(e.target.value);
                  setPasswordError('');
                }} className={`h-11 bg-gray-50 border-gray-300 focus:border-primary pr-10 ${confirmPassword && newPassword !== confirmPassword ? 'border-red-500' : ''}`} required />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirmPassword && newPassword !== confirmPassword && <p className="text-xs text-red-500">As senhas não coincidem</p>}
              </div>

              <Button type="submit" className="w-full h-11 bg-primary hover:bg-primary/90 text-white font-semibold" disabled={isLoading || newPassword.length < 6}>
                {isLoading ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isInviteMode ? 'Criando conta...' : 'Salvando...'}
                  </> : isInviteMode ? 'Criar Conta e Entrar' : 'Atualizar Senha'}
              </Button>
            </CardContent>
          </form>
        </Card>
      </div>;
  }
  return <div className="bg-white relative overflow-hidden w-full min-h-screen">


      {/* Premium gradient background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              'linear-gradient(to right, #1e293b 1px, transparent 1px), linear-gradient(to bottom, #1e293b 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        <div className="absolute -top-40 -right-40 w-[70vw] max-w-[600px] aspect-square bg-gradient-to-br from-blue-400/20 to-purple-500/20 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -left-40 w-[60vw] max-w-[500px] aspect-square bg-gradient-to-br from-blue-500/15 to-indigo-500/15 rounded-full blur-3xl" />
      </div>

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-gray-200/60 h-16">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 h-full">
          <div className="flex items-center justify-between h-full gap-4">
            <button onClick={() => setActiveTab('inicio')} className="flex items-center gap-3 group shrink-0">
              <div className="w-9 h-9 bg-gradient-to-br from-slate-900 to-blue-900 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:shadow-blue-500/50 transition-all shrink-0">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-gray-900 tracking-tight whitespace-nowrap">Radar Financeiro</span>
            </button>

            <nav className="flex items-center gap-2 shrink-0">
              {(() => {
                const inactiveCls = "border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 hover:border-gray-400 font-medium rounded-lg transition-all whitespace-nowrap px-5";
                const bluePrimary = "bg-gradient-to-r from-slate-900 to-blue-900 hover:from-slate-800 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-[1.02] transition-all whitespace-nowrap px-5";
                return (
                  <>
                    <Button onClick={() => setActiveTab('inicio')} className={inactiveCls}>
                      Início
                    </Button>
                    <Button onClick={() => setActiveTab('planos')} className={bluePrimary}>
                      Ver planos
                    </Button>
                  </>
                );
              })()}
            </nav>
          </div>
        </div>
      </header>

      {/* Login Modal */}
      {showLoginModal && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setShowLoginModal(false)}
        >
          <div className="relative w-full max-w-md animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className="absolute -inset-1 bg-gradient-to-br from-blue-500/40 to-indigo-500/40 rounded-2xl blur-xl" />
            <Card className="relative bg-white border border-gray-200 shadow-2xl rounded-2xl">
              <button
                onClick={() => setShowLoginModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors text-2xl leading-none w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 z-10"
                aria-label="Fechar"
              >
                ×
              </button>
              {showForgotPassword ? (
                <form onSubmit={handleForgotPassword}>
                  <CardHeader className="text-center space-y-2 pt-8">
                    <CardTitle className="text-2xl font-bold text-gray-900">Recuperar Senha</CardTitle>
                    <CardDescription className="text-gray-600">Digite seu email para receber o link</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email" className="text-gray-700">Email</Label>
                      <Input id="forgot-email" type="email" placeholder="seu@email.com" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} className="h-12 bg-gray-50 border-gray-200 focus:border-blue-600 rounded-xl" required />
                    </div>
                    <Button type="submit" className="w-full h-12 bg-gradient-to-r from-slate-900 to-blue-900 hover:from-slate-800 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30" disabled={isLoading}>
                      {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enviando...</> : 'Enviar link de recuperação'}
                    </Button>
                    <button type="button" onClick={() => setShowForgotPassword(false)} className="w-full text-center text-sm text-blue-600 hover:text-blue-700">Voltar ao login</button>
                  </CardContent>
                </form>
              ) : (
                <form onSubmit={handleLogin}>
                  <CardHeader className="text-center space-y-2 pt-8">
                    <div className="mx-auto w-12 h-12 bg-gradient-to-br from-slate-900 to-blue-900 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30 mb-2">
                      <BarChart3 className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-gray-900">Entre na sua conta</CardTitle>
                    <CardDescription className="text-gray-600">Acesse seu dashboard financeiro</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="text-gray-700">Email</Label>
                      <Input id="login-email" type="email" placeholder="seu@email.com" value={loginData.email} onChange={e => setLoginData(prev => ({ ...prev, email: e.target.value }))} className="h-12 bg-gray-50 border-gray-200 focus:border-blue-600 rounded-xl" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password" className="text-gray-700">Senha</Label>
                      <div className="relative">
                        <Input id="login-password" type={showLoginPassword ? "text" : "password"} placeholder="Sua senha" value={loginData.password} onChange={e => setLoginData(prev => ({ ...prev, password: e.target.value }))} className="h-12 bg-gray-50 border-gray-200 focus:border-blue-600 pr-10 rounded-xl" required />
                        <button type="button" onClick={() => setShowLoginPassword(!showLoginPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                          {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <button type="button" onClick={() => setShowForgotPassword(true)} className="text-sm text-blue-600 hover:text-blue-700">Esqueci minha senha</button>
                    <Button type="submit" className="w-full h-12 bg-gradient-to-r from-slate-900 to-blue-900 hover:from-slate-800 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-all" disabled={isLoading}>
                      {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Entrando...</> : 'Entrar'}
                    </Button>
                  </CardContent>
                </form>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="w-full pt-16 relative z-10">
        {activeTab === 'inicio' && (
          <div className="animate-in fade-in duration-500 flex flex-col lg:h-[calc(100vh-4rem)] lg:overflow-hidden">
            <section className="relative w-full px-6 lg:px-12 pt-10 pb-10 flex-1 min-h-0 flex items-center">
              <div className="w-full max-w-screen-2xl mx-auto grid lg:grid-cols-12 gap-10 xl:gap-16 items-center">
                {/* LEFT COLUMN — Texts + Cards */}
                <div className="lg:col-span-7 xl:col-span-8 flex flex-col gap-7">
                  <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.2] tracking-tight pb-1 bg-gradient-to-r from-slate-900 to-blue-900 bg-clip-text text-transparent">
                    <span className="block">Controle financeiro de verdade.</span>
                    <span className="block">Sem planilhas.</span>
                    <span className="block">Sem bagunça.</span>
                  </h1>

                  <p className="text-base lg:text-lg text-gray-600 max-w-3xl leading-relaxed">
                    Centralize lançamentos, acompanhe indicadores e antecipe seu fluxo de caixa com a clareza visual de uma fintech premium.
                  </p>

                  {/* VALUE CARDS — destacados */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
                    {[
                      { icon: BarChart3, title: 'Visão completa', desc: 'DRE, DFC e fluxo numa tela.' },
                      { icon: ListChecks, title: 'Lançamentos rápidos', desc: 'Receitas e despesas em cliques.' },
                      { icon: PieChart, title: 'Relatórios prontos', desc: 'Análises gerenciais com 1 clique.' },
                      { icon: Wallet, title: 'Saldos bancários', desc: 'Conciliação em tempo real.' },
                    ].map((item, i) => (
                      <div
                        key={i}
                        className="group relative bg-white border border-gray-200 rounded-2xl p-5 shadow-md hover:shadow-2xl hover:border-blue-300 hover:-translate-y-1 transition-all duration-300"
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/0 to-indigo-50/0 group-hover:from-blue-50/60 group-hover:to-indigo-50/30 rounded-2xl transition-all" />
                        <div className="relative">
                          <div className="w-11 h-11 bg-gradient-to-br from-slate-900 to-blue-900 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30 mb-3 group-hover:scale-110 group-hover:rotate-3 transition-transform">
                            <item.icon className="w-5 h-5 text-white" />
                          </div>
                          <h3 className="text-sm font-bold text-gray-900 mb-1">{item.title}</h3>
                          <p className="text-xs text-gray-600 leading-snug">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* RIGHT COLUMN — Login imponente */}
                <div className="lg:col-span-5 xl:col-span-4 flex items-center justify-center">
                  <div className="relative w-full max-w-md">
                    <div className="absolute -inset-1.5 bg-gradient-to-br from-blue-500/40 to-indigo-500/40 rounded-3xl blur-2xl" />
                    <Card className="relative bg-white border border-gray-200 shadow-2xl rounded-2xl">
                      {showForgotPassword ? (
                        <form onSubmit={handleForgotPassword}>
                          <CardHeader className="text-center space-y-2 pt-10">
                            <CardTitle className="text-3xl font-bold text-gray-900">Recuperar Senha</CardTitle>
                            <CardDescription className="text-gray-600">Digite seu email para receber o link</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-5 pb-10">
                            <div className="space-y-2">
                              <Label htmlFor="forgot-email-inline" className="text-gray-700">Email</Label>
                              <Input id="forgot-email-inline" type="email" placeholder="seu@email.com" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} className="h-14 text-base bg-gray-50 border-gray-200 focus:border-blue-600 rounded-xl" required />
                            </div>
                            <Button type="submit" className="w-full h-14 text-base bg-gradient-to-r from-slate-900 to-blue-900 hover:from-slate-800 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30" disabled={isLoading}>
                              {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enviando...</> : 'Enviar link de recuperação'}
                            </Button>
                            <button type="button" onClick={() => setShowForgotPassword(false)} className="w-full text-center text-sm text-blue-600 hover:text-blue-700">Voltar ao login</button>
                          </CardContent>
                        </form>
                      ) : (
                        <form onSubmit={handleLogin}>
                          <CardHeader className="text-center space-y-2 pt-10 pb-4">
                            <div className="mx-auto w-14 h-14 bg-gradient-to-br from-slate-900 to-blue-900 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 mb-1">
                              <BarChart3 className="w-7 h-7 text-white" />
                            </div>
                            <CardTitle className="text-3xl font-bold text-gray-900">Acesse sua conta</CardTitle>
                            <CardDescription className="text-gray-600">Entre no seu dashboard financeiro</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-5 pb-10">
                            <div className="space-y-2">
                              <Label htmlFor="login-email-inline" className="text-gray-700 text-sm">Email</Label>
                              <Input id="login-email-inline" type="email" placeholder="seu@email.com" value={loginData.email} onChange={e => setLoginData(prev => ({ ...prev, email: e.target.value }))} className="h-14 text-base bg-gray-50 border-gray-200 focus:border-blue-600 rounded-xl" required />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="login-password-inline" className="text-gray-700 text-sm">Senha</Label>
                              <div className="relative">
                                <Input id="login-password-inline" type={showLoginPassword ? "text" : "password"} placeholder="Sua senha" value={loginData.password} onChange={e => setLoginData(prev => ({ ...prev, password: e.target.value }))} className="h-14 text-base bg-gray-50 border-gray-200 focus:border-blue-600 pr-10 rounded-xl" required />
                                <button type="button" onClick={() => setShowLoginPassword(!showLoginPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                                  {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                              </div>
                            </div>
                            <div className="flex justify-end">
                              <button type="button" onClick={() => setShowForgotPassword(true)} className="text-xs text-blue-600 hover:text-blue-700 font-medium">Esqueci minha senha</button>
                            </div>
                            <Button type="submit" className="w-full h-14 text-base bg-gradient-to-r from-slate-900 to-blue-900 hover:from-slate-800 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-[1.01] transition-all" disabled={isLoading}>
                              {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Entrando...</> : 'Entrar'}
                            </Button>
                            <div className="flex items-center justify-center gap-3 pt-1 text-[11px] text-gray-500">
                              <span className="flex items-center gap-1"><Shield className="w-3 h-3 text-emerald-600" /> Conexão segura</span>
                              <span className="w-1 h-1 rounded-full bg-gray-300" />
                              <span>Conformidade LGPD</span>
                            </div>
                          </CardContent>
                        </form>
                      )}
                    </Card>
                  </div>
                </div>
              </div>
            </section>

            {/* DARK IMPACT SECTION — bottom band with CTA */}
            <section className="relative w-full px-6 lg:px-12 py-6 lg:py-7 bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 overflow-hidden shrink-0">
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage:
                    'radial-gradient(circle at 20% 50%, rgba(59,130,246,0.5) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(139,92,246,0.5) 0%, transparent 50%)',
                }}
              />
              <div
                className="absolute inset-0 opacity-[0.04]"
                style={{
                  backgroundImage:
                    'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
                  backgroundSize: '50px 50px',
                }}
              />
              <div className="relative max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-5 text-center md:text-left">
                <div className="space-y-1">
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight tracking-tight">
                    Pare de apagar incêndios.{' '}
                    <span className="bg-gradient-to-r from-blue-400 to-indigo-300 bg-clip-text text-transparent">
                      Comece a prever resultados.
                    </span>
                  </h2>
                  <p className="text-sm md:text-base text-slate-300 leading-relaxed">
                    A clareza financeira que sua empresa precisa para crescer com segurança.
                  </p>
                </div>
                <Button
                  onClick={() => setActiveTab('planos')}
                  className="shrink-0 h-12 px-7 bg-white text-blue-700 hover:bg-blue-50 font-bold rounded-xl shadow-2xl shadow-blue-500/30 hover:scale-[1.03] transition-all whitespace-nowrap"
                >
                  Ver planos
                  <Sparkles className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </section>
          </div>
        )}

        {/* Planos Tab */}
        {activeTab === 'planos' && (
          <div className="animate-in fade-in duration-500 max-w-6xl mx-auto space-y-12 px-6 lg:px-10 py-20">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-1.5 rounded-full text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                Planos pensados para sua empresa
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight">
                Escolha o plano <span className="bg-gradient-to-r from-slate-900 to-blue-900 bg-clip-text text-transparent">ideal</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
                Comece hoje e tenha controle total das suas finanças com a clareza de uma fintech premium.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-stretch pt-4">
              {/* Basic Plan */}
              <Card
                className={`relative bg-white text-slate-900 overflow-hidden transition-all duration-300 cursor-pointer flex flex-col h-full rounded-2xl ${
                  selectedPlan === 'basic'
                    ? 'border-2 border-blue-600 shadow-xl ring-4 ring-blue-100'
                    : 'border border-gray-200 shadow-md hover:shadow-xl hover:-translate-y-1'
                }`}
                onClick={() => setSelectedPlan('basic')}
              >
                <CardHeader className="text-center pb-4 pt-8">
                  <CardTitle className="text-xl font-bold text-gray-900">Basic</CardTitle>
                  <CardDescription className="text-gray-600 text-sm mt-2">
                    Para pequenos e médios negócios
                  </CardDescription>
                  <div className="pt-6">
                    <div className="flex items-baseline justify-center gap-2">
                      <span className="text-5xl font-bold text-gray-900">R$ 129,90</span>
                      <span className="text-gray-500 font-medium">/mês</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col flex-1 px-8 pb-8">
                  <div className="space-y-3 py-6 border-t border-gray-100">
                    {['Acesso mensal ao aplicativo', 'Controle financeiro completo', 'Relatórios gerenciais'].map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                          <Check className="h-3 w-3 text-blue-600" strokeWidth={3} />
                        </div>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex-grow" />
                  <Button
                    className="w-full h-12 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-xl shadow-lg hover:scale-[1.02] transition-all"
                    onClick={e => {
                      e.stopPropagation();
                      handlePlanRedirect('https://buy.stripe.com/cNi9AV3Gl1f41w74WbeME01');
                    }}
                  >
                    Começar com Basic
                  </Button>
                </CardContent>
              </Card>

              {/* Plus Plan */}
              <Card
                className={`relative bg-white text-slate-900 overflow-hidden transition-all duration-300 cursor-pointer flex flex-col h-full rounded-2xl ${
                  selectedPlan === 'plus'
                    ? 'border-2 border-blue-900 shadow-xl ring-4 ring-blue-100'
                    : 'border border-gray-200 shadow-md hover:shadow-xl hover:-translate-y-1'
                }`}
                onClick={() => setSelectedPlan('plus')}
              >
                <CardHeader className="text-center pb-4 pt-8">
                  <CardTitle className="text-xl font-bold text-gray-900">Plus</CardTitle>
                  <CardDescription className="text-gray-600 text-sm mt-2">
                    Tudo do Basic + Business Intelligence
                  </CardDescription>
                  <div className="pt-6">
                    <div className="flex items-baseline justify-center gap-2">
                      <span className="text-5xl font-bold text-gray-900">R$ 499,90</span>
                      <span className="text-gray-500 font-medium">/mês</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col flex-1 px-8 pb-8">
                  <div className="space-y-3 py-6 border-t border-gray-100">
                    {['Tudo do plano Basic', 'BI personalizado', 'Dashboards avançados', 'Análises preditivas', 'Suporte prioritário', 'Consultoria estratégica'].map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                          <Check className="h-3 w-3 text-blue-700" strokeWidth={3} />
                        </div>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex-grow" />
                  <Button
                    className="w-full h-12 bg-gradient-to-r from-slate-900 to-blue-900 hover:from-slate-800 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:scale-[1.02] transition-all"
                    onClick={e => {
                      e.stopPropagation();
                      handlePlanRedirect('https://buy.stripe.com/00weVf1yd1f4a2D0FVeME00');
                    }}
                  >
                    Começar com Plus
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>;

}